import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "../../axios-config";
import { Store } from '../../models/store';
import StoreList from './StoreList';
import StoreForm from './StoreForm';
import { StoreTypesProvider } from '../../contexts/StoreTypesContext';
import { Store as StoreIcon, Plus } from 'lucide-react';

const StoreEditor: React.FC = () => {
    const [stores, setStores] = useState<Store[]>([]);
    const [editingStore, setEditingStore] = useState<Store | null>(null);
    const [showForm, setShowForm] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchStores();
    }, []);

    const fetchStores = async () => {
        try {
            const res = await axios.get('/api/stores');
            setStores(res.data);
        } catch (error) {
            console.error('Error fetching stores:', error);
        }
    };

    const handleSuccess = () => {
        setShowForm(false);
        setEditingStore(null);
        fetchStores();
    };

    const handleCreate = () => {
        setEditingStore(null);
        setShowForm(true);
    };

    const handleEdit = (store: Store) => {
        setEditingStore(store);
        setShowForm(true);
    };

    const handleDelete = async (storeId: number) => {
        try {
            await axios.delete(`/api/stores/${storeId}`, { withCredentials: true });
            setStores(stores.filter(s => s.storeId !== storeId));
        } catch (error) {
            console.error('Error deleting store:', error);
        }
    };

    const handleProducts = (storeId: number) => {
        navigate(`/store/${storeId}/products`);
    };

    return (
        <StoreTypesProvider>
            <div className="space-y-6 p-4 rounded-xl">
                <div className="bg-gradient-to-br from-indigo-50 to-blue-100 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <StoreIcon className="w-6 h-6 text-indigo-500" />
                            <h2 className="text-xl font-semibold text-gray-800">Stores</h2>
                        </div>
                        <button 
                            onClick={handleCreate} 
                            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Add Store
                        </button>
                    </div>

                    {showForm && (
                        <StoreForm
                            storeId={editingStore ? editingStore.storeId : undefined}
                            onSuccess={handleSuccess}
                            onCancel={() => setShowForm(false)}
                        />
                    )}
                    <StoreList
                        stores={stores}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onProducts={handleProducts}
                    />
                </div>
            </div>
        </StoreTypesProvider>
    );
};

export default StoreEditor;
